/**
 * The `one_of` value: a marshalled JSON array of strings, which the editor
 * works with as a list of entries.
 *
 * The backend expects the array form on the wire, so every write goes through
 * {@link entriesToOneOfValue}, and every read through
 * {@link oneOfValueToEntries}.
 */

import { isOneOfOperator } from './operators';

/**
 * The entries a stored value holds. A blank value, malformed JSON and any JSON
 * that is not an array all hold none; a non-string entry is stringified.
 */
export function oneOfValueToEntries(value: string | null): string[] {
  if (value == null || value === '') {
    return [];
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return [];
  }
  return Array.isArray(parsed) ? parsed.map((entry) => String(entry)) : [];
}

/**
 * The stored value for a list of entries: each entry trimmed, blank entries
 * dropped, and null when nothing is left, so an emptied editor writes no value.
 */
export function entriesToOneOfValue(entries: readonly string[]): string | null {
  const cleaned = entries.map((entry) => entry.trim()).filter((entry) => entry !== '');
  return cleaned.length === 0 ? null : JSON.stringify(cleaned);
}

/**
 * Whether a value holds no usable entries: "[]", a list of blanks and a value
 * that does not parse as a list are all blank.
 */
export function isOneOfValueBlank(value: string | null): boolean {
  return entriesToOneOfValue(oneOfValueToEntries(value)) == null;
}

/**
 * Splits typed or pasted text into entries on commas, trimming each and
 * dropping the blanks, so a pasted "100, 101, 102" becomes three entries
 * rather than one.
 */
export function splitOneOfInput(input: string): string[] {
  return input
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry !== '');
}

/**
 * Reshapes a value when the operator crosses into or out of `one_of`.
 *
 * A scalar becomes a single-entry list on the way in. On the way out, a
 * single-entry list unwraps to its entry, and a longer list clears, since no
 * one scalar stands for it. A switch that stays on the same side keeps the
 * value as it is.
 */
export function oneOfValueForOperatorChange(
  nextOperator: string,
  prevOperator: string,
  value: string | null,
): string | null {
  const wasOneOf = isOneOfOperator(prevOperator);
  const isOneOf = isOneOfOperator(nextOperator);
  if (wasOneOf === isOneOf) {
    return value;
  }
  if (isOneOf) {
    return value == null ? null : entriesToOneOfValue([value]);
  }
  const entries = oneOfValueToEntries(value);
  return entries.length === 1 ? (entries[0] ?? null) : null;
}
